import clone from 'lodash/clone';
import { useCallback, useEffect, useState } from 'react';

import { ASSET_STATUSES, ASSETS } from './constants';
import {
  IAssetsData,
  ITechnicalReviewLogic,
  ITechnicalReviewProps,
  ITechnicalReviewState,
} from './types';
import { isDataLoaded, isFetchingOfDataNeeded } from './utils';

export const useTechnicalReviewState = (): ITechnicalReviewState => {
  const [isActive, setIsActive] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [toggleIconVisibility, setToggleIconVisibility] = useState<{
    [groupName: string]: { [reviewId: string]: boolean };
  }>({});
  const [editReviewsMode, setEditReviewsMode] = useState(false);

  const handleOpen = useCallback(() => {
    setIsActive(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsActive(false);
    setEditReviewsMode(false);
    setToggleIconVisibility({});
  }, []);

  const handleStartEditIcons = useCallback(() => {
    setEditReviewsMode(true);
  }, []);

  const handleCancelEditIcons = useCallback(() => {
    setEditReviewsMode(false);
    setToggleIconVisibility({});
  }, []);

  const setActiveGroupHandler = useCallback((name: string) => {
    setActiveGroup(name);
  }, []);

  return {
    isActive,
    activeGroup,
    toggleIconVisibility,
    editReviewsMode,
    handleOpen,
    handleClose,
    handleStartEditIcons,
    handleCancelEditIcons,
    setActiveGroupHandler,
    setActiveGroup,
    setToggleIconVisibility,
    setEditReviewsMode,
  };
};

export const useTechnicalReviewLogic = (
  props: ITechnicalReviewProps,
  state: ITechnicalReviewState,
): ITechnicalReviewLogic => {
  const {
    isActive,
    activeGroup,
    toggleIconVisibility,
    setActiveGroup,
    setToggleIconVisibility,
  } = state;

  const serializeReviewsGroupsData = useCallback((): IAssetsData => {
    const serialisedData = clone(props.assets.data);

    for (const groupName in toggleIconVisibility) {
      const changedGroupValues = toggleIconVisibility[groupName];
      for (const reviewId in changedGroupValues) {
        const review = serialisedData.reviews[reviewId],
          value = changedGroupValues[reviewId];
        if (value) {
          review.groups[groupName] = review.groups[groupName] || null;
        } else {
          delete review.groups[groupName];
        }
      }
    }
    return serialisedData;
  }, [props, toggleIconVisibility]);

  const handleSubmitEditedIcons = useCallback(() => {
    state.setEditReviewsMode(false);
    props.dispatchAssetsChange(serializeReviewsGroupsData());
  }, [props.dispatchAssetsChange, serializeReviewsGroupsData, state]);

  const handleReviewStatusToggle = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (event.currentTarget.disabled) {
        return;
      }

      const review = event.currentTarget.getAttribute('data-review'),
        action = event.currentTarget.getAttribute('data-action');

      const val = `review=${review}&action=${action}`;
      if (!review || !action) {
        console.error(`Data is missing review=${review}; action=${action};`);
        return;
      }
      props.dispatchAssetStatusChange(val);
    },
    [props.dispatchAssetStatusChange],
  );

  const handleIconVisibilityToggle = useCallback(
    (value: boolean, toggleId: string) => {
      if (activeGroup) {
        setToggleIconVisibility(prevState => {
          const newState = {
            [activeGroup]: {},
            ...prevState,
          };
          newState[activeGroup][toggleId] = value;
          return newState;
        });
      } else {
        console.error(
          `Active group is not selected. Can't set value for ${toggleId}`,
        );
      }
    },
    [activeGroup, setToggleIconVisibility],
  );

  const handleIconVisibilityToggleAll = useCallback(
    (value: boolean) => {
      if (activeGroup) {
        setToggleIconVisibility(prevState => {
          const newState = {
            [activeGroup]: {},
            ...prevState,
          };
          if (props.assets.data?.reviews) {
            for (const reviewId in props.assets.data.reviews) {
              newState[activeGroup][reviewId] = value;
            }
          }
          return newState;
        });
      } else {
        console.error(
          `Active group is not selected. Can't perform selection/deselection.`,
        );
      }
    },
    [activeGroup, props, setToggleIconVisibility],
  );

  const fetchDataIfNeeded = useCallback(() => {
    for (const key of [ASSET_STATUSES, ASSETS]) {
      const dataStates = {
        [ASSETS]: props.assets,
        [ASSET_STATUSES]: props.assetStatuses,
      };
      if (
        isFetchingOfDataNeeded(
          key,
          dataStates,
          isActive,
          props.reportPk,
          props.officeCode,
        )
      ) {
        props.dispatchFetch[key](props.reportPk);
      }
    }
  }, [isActive, props]);

  const setInitialGroupTab = useCallback(() => {
    if (props.assets.data?.groups) {
      const groupKeys = props.assets.data.groups.map(group => group.name);
      if (groupKeys.length) {
        setActiveGroup(groupKeys[0]);
      }
    }
  }, [props.assets.data, setActiveGroup]);

  useEffect(() => {
    fetchDataIfNeeded();
  }, [fetchDataIfNeeded]);

  useEffect(() => {
    const dataStates = { [ASSETS]: props.assets };
    if (!activeGroup && isDataLoaded(ASSETS, dataStates)) {
      setInitialGroupTab();
    }
  }, [activeGroup, props.assets, setInitialGroupTab]);

  return {
    serializeReviewsGroupsData,
    handleSubmitEditedIcons,
    handleReviewStatusToggle,
    handleIconVisibilityToggle,
    handleIconVisibilityToggleAll,
  };
};
